type RequestBatcherMessage<ItemIn, ItemOut> = {
	item: ItemIn
	signal?: AbortSignal
	promise: Promise<ItemOut>
	resolve: (res: ItemOut) => void
	reject: (err: unknown) => void
}

export class RequestBatcher<ItemIn, ItemOut> {
	messages: Array<RequestBatcherMessage<ItemIn, ItemOut>>
	timer: NodeJS.Timeout | null

	constructor(
		private batchFn: (items: Array<ItemIn>) => Promise<Array<ItemOut>>,
		private timeout: number = 150,
	) {
		this.messages = []
		this.timer = null
	}

	load = async (item: ItemIn, signal?: AbortSignal): Promise<ItemOut> => {
		if (!this.timer) {
			this.timer = setTimeout(this.executeBatch, this.timeout)
		}

		const message = { item, signal } as RequestBatcherMessage<ItemIn, ItemOut>
		message.promise = new Promise((resolve, reject) => {
			message.resolve = resolve
			message.reject = reject
		})

		this.messages.push(message)
		return message.promise
	}

	executeBatch = async () => {
		if (this.timer) {
			clearTimeout(this.timer)
			this.timer = null
		}

		const batchedMessages = this.messages.filter(message => !message.signal?.aborted)
		this.messages = []

		if (batchedMessages.length === 0) {
			return
		}

		try {
			const results = await this.batchFn(batchedMessages.map(message => message.item))

			if (results.length !== batchedMessages.length) {
				throw new Error(
					`Batch function returned ${results.length} results, but ${batchedMessages.length} were expected`,
				)
			}

			batchedMessages.forEach((message, index) => {
				message.resolve(results[index])
			})
		} catch (error) {
			batchedMessages.forEach(message => {
				message.reject(error)
			})
		}
	}
}
